import type { Contract } from '@entities/legal/types';
import type { ArchiveInfoResult, RecordKind } from './types';

export const PAGE_SIZE = 20;

export const ARCHIVE_ABANDONED_RETENTION_DAYS = 30;
export const DAY_MS = 24 * 60 * 60 * 1000;

export function formatDate(value: string | null): string {
  if (!value) return '—';
  return new Date(value).toLocaleDateString('ru-RU');
}

/** Причина архива и срок удаления для строки во вкладке «Архив». Заключённый
 *  (status='concluded') хранится 3 года после окончания договора-наследника —
 *  точную дату здесь не считаем (зависит от договора), показываем правило.
 *  Брошенный удаляется через 30 дней в архиве — дату знаем точно. */
export function archiveInfo(c: Contract): ArchiveInfoResult {
  if (c.status === 'concluded') {
    return { reason: 'Заключён', when: 'хранится 3 года после окончания договора' };
  }
  const del = c.archived_at
    ? new Date(new Date(c.archived_at).getTime() + ARCHIVE_ABANDONED_RETENTION_DAYS * DAY_MS)
    : null;
  return {
    reason: 'Брошен',
    when: del ? `удалится ${del.toLocaleDateString('ru-RU')}` : '—',
  };
}

export const STATUS_OPTIONS = [
  { value: '', label: 'Любой' },
  { value: 'draft', label: 'Черновик' },
  { value: 'under_review', label: 'На проверке' },
  { value: 'analyzed', label: 'Проанализирован' },
  { value: 'signed', label: 'Подписан' },
  { value: 'expired', label: 'Истёк' },
  { value: 'terminated', label: 'Расторгнут' },
];

export const SORT_OPTIONS = [
  { value: 'created_at', label: 'По дате добавления' },
  { value: 'counterparty', label: 'По контрагенту' },
  { value: 'sum', label: 'По сумме' },
  { value: 'expires_at', label: 'По дате окончания' },
  { value: 'status', label: 'По статусу' },
];

/** Копирайт реестра по виду записи — заголовки, подсказки, пустые состояния
 *  отличаются, а вся логика/разметка общая. */
export const KIND_COPY: Record<
  RecordKind,
  {
    basePath: string;
    intro: string;
    newButton: string;
    modalTitle: string;
    emptyDefault: string;
  }
> = {
  contract: {
    basePath: 'contracts',
    intro:
      'Реестр договоров с автоматической правовой экспертизой: контрагент присылает проект договора с приложениями (Word, PDF, Excel, скан — можно папкой или архивом), агент извлекает текст и выделяет риски по 7 категориям (предмет, цена, сроки, ответственность, гарантии, форс-мажор, подсудность).',
    newButton: 'Новый договор',
    modalTitle: 'Новый договор',
    emptyDefault: 'Договоров пока нет — нажмите «Новый договор», чтобы добавить.',
  },
  precontract: {
    basePath: 'precontracts',
    intro:
      'Преддоговорная работа — незаключённые договоры на стадии проработки и согласования. Та же правовая экспертиза и риски, что и в основном реестре; когда договор подписан, переведите его в «Договоры» кнопкой «Договор заключён» в карточке.',
    newButton: 'Добавить в проработку',
    modalTitle: 'Новый документ на проработку',
    emptyDefault: 'В проработке пока пусто — нажмите «Добавить в проработку», чтобы завести проект.',
  },
};
